/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\SystemGeneralTabCodecs.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the System General module.
----------------------------------------------------------------*/


import type { ReactNode } from 'react';
import { Music } from 'lucide-react';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { CodecField } from '../../../components/ui/CodecField';
import { useI18n } from '../../../hooks/useI18n';
import { SystemGeneralSettings } from '../types/systemGeneral.types';

interface Props {
    formData: SystemGeneralSettings;
    renderField: (field: keyof SystemGeneralSettings, control: ReactNode) => ReactNode;
    onChange: (field: keyof SystemGeneralSettings, value: SystemGeneralSettings[keyof SystemGeneralSettings]) => void;
}

export function SystemGeneralTabCodecs({ formData, renderField, onChange }: Props) {
    const { t } = useI18n();

    return (
        <FormBox title={t('systemGeneral.tabs.codecs')} icon={<Music className="h-5 w-5" />}>
            <div className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                    {/* Codec preferences */}
                    {renderField('inboundCodecPrefs', <CodecField value={formData.inboundCodecPrefs} onChange={(v) => onChange('inboundCodecPrefs', v)} allowVideo={false} />)}
                    {renderField('outboundCodecPrefs', <CodecField value={formData.outboundCodecPrefs} onChange={(v) => onChange('outboundCodecPrefs', v)} allowVideo={false} />)}
                    {renderField('maxRegistrations', <Input type="number" min={1} max={20} value={formData.maxRegistrations} onChange={(e) => onChange('maxRegistrations', parseInt(e.target.value, 10))} />)}

                    {renderField('dtmfMode',
                        <select value={formData.dtmfMode} onChange={(e) => onChange('dtmfMode', e.target.value)} className="px-3 py-2 text-sm border border-border rounded bg-background text-foreground w-full">
                            <option value="rfc4733">{t('systemGeneral.options.dtmfMode.rfc4733', 'RFC 4733')}</option>
                            <option value="rfc2833">{t('systemGeneral.options.dtmfMode.rfc2833', 'RFC 2833')}</option>
                            <option value="info">{t('systemGeneral.options.dtmfMode.info', 'SIP INFO')}</option>
                            <option value="inband">{t('systemGeneral.options.dtmfMode.inband', 'Inband')}</option>
                        </select>
                    )}

                    <div className="col-span-full border-t border-border my-2" />

                    {/* Music & Media */}
                    {renderField('holdMusic', <Input value={formData.holdMusic} onChange={(e) => onChange('holdMusic', e.target.value)} placeholder="local_stream://default" />)}
                    {renderField('ringtone', <Input value={formData.ringtone} onChange={(e) => onChange('ringtone', e.target.value)} placeholder={t('systemGeneral.placeholders.ringtone', 'Optional')} />)}
                </div>
            </div>
        </FormBox>
    );
}
